import { PenTool, Pencil, Spline } from 'lucide-react';
import type { PenSettings, PenVariant, ShapeFillValue } from '../types';
import { ColorSwatchPopover, type ColorValue } from './ColorSwatchPopover';

interface PenPanelProps {
  settings: PenSettings;
  onChange: (settings: Partial<PenSettings>) => void;
  /** Recent-colors strip — passed through to the ColorSwatchPopover
   *  for stroke and fill. Optional; omitting hides the strip. */
  recentColors?: string[];
  onRecentColorPicked?: (color: string) => void;
  /** Trigger Edit Path mode on a currently-selected vector layer.
   *  Only visible when `editPathAvailable` is true (the parent
   *  passes this when the active layer is a vector). Alternative
   *  entry to the LayerOverflowMenu's "Edit Path…" item. */
  onEditActivePath?: () => void;
  editPathAvailable?: boolean;
}

/** Pen-tool properties panel — Tier 2 Group A. Variant chip row at
 *  the top picks between the three creation modes; the section
 *  below shows variant-shared path settings (stroke, fill, auto-
 *  add/delete) plus the variant-specific controls. */
const VARIANT_CHIPS: {
  id: PenVariant;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: 'pen', label: 'Pen', icon: PenTool },
  { id: 'freeform', label: 'Freeform', icon: Pencil },
  { id: 'curvature', label: 'Curvature', icon: Spline },
];

/** Cycle order for the future Shift+P keyboard variant cycle.
 *  Mirrors the RetouchPanel's chip → cycle pattern so the cycle
 *  order and the visual order can never drift. */
export const PEN_VARIANT_ORDER: readonly PenVariant[] = VARIANT_CHIPS.map((c) => c.id);

const SECTION_CLASS = 'flex flex-col gap-2';

/** Convert the workspace's `string | ShapeFillValue` fill type
 *  into the `ColorValue` shape the swatch popover wants. Mirrors
 *  the conversion `PropertiesPanel` uses for the existing fill
 *  pickers — kept local here to avoid pulling in the helper. */
function toColorValue(fill: string | ShapeFillValue): ColorValue {
  if (typeof fill === 'string') {
    return fill === 'transparent'
      ? { type: 'solid', color: 'transparent' }
      : { type: 'solid', color: fill };
  }
  return fill;
}

function fromColorValue(v: ColorValue): string | ShapeFillValue {
  if (v.type === 'solid') return v.color;
  return v;
}

export function PenPanel({
  settings,
  onChange,
  recentColors,
  onRecentColorPicked,
  onEditActivePath,
  editPathAvailable,
}: PenPanelProps) {
  return (
    <div className={SECTION_CLASS}>
      {/* Variant chips. Three options fit comfortably in a single
          row at the panel width — no scrolling needed. */}
      <div className="grid grid-cols-3 gap-1.5">
        {VARIANT_CHIPS.map((chip) => {
          const Icon = chip.icon;
          const active = settings.variant === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              title={chip.label}
              aria-label={chip.label}
              aria-pressed={active}
              className={`flex flex-col items-center justify-center gap-0.5 h-12 rounded-md transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => onChange({ variant: chip.id })}
            >
              <Icon className="h-4 w-4" />
              <span className="text-[10px] leading-none">{chip.label}</span>
            </button>
          );
        })}
      </div>

      {/* Shared path settings. */}
      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-medium text-muted-foreground uppercase">Path</h4>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={settings.autoAddDelete}
            onChange={(e) => onChange({ autoAddDelete: e.target.checked })}
            className="w-3.5 h-3.5"
          />
          <span>Auto-add / delete anchors</span>
        </label>
        <label className="flex items-start gap-2">
          <span className="text-xs w-14 pt-2">Stroke</span>
          <ColorSwatchPopover
            value={{ type: 'solid', color: settings.strokeColor }}
            onChange={(v) => v.type === 'solid' && onChange({ strokeColor: v.color })}
            allowGradient={false}
            recentColors={recentColors}
            onRecentColorPicked={onRecentColorPicked}
            label="Stroke color"
          />
          {/* The ColorSwatchPopover renders `'transparent'` correctly
              but its hex input rejects the keyword, so once the user
              picks a color there's no path back through that
              popover. This affordance closes the loop. */}
          <button
            type="button"
            onClick={() => onChange({ strokeColor: 'transparent' })}
            className="text-[10px] px-2 py-1 rounded border border-border hover:bg-accent text-muted-foreground"
            title="Set stroke to transparent (no outline)"
          >
            None
          </button>
        </label>
        <label className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground w-14">Width</span>
          <input
            type="range"
            min={0}
            max={40}
            value={settings.strokeWidth}
            onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
            className="flex-1"
          />
          <span className="w-8 text-right tabular-nums">{settings.strokeWidth}</span>
        </label>
        <label className="flex items-start gap-2">
          <span className="text-xs w-14 pt-2">Fill</span>
          <ColorSwatchPopover
            value={toColorValue(settings.fill)}
            onChange={(v) => onChange({ fill: fromColorValue(v) })}
            allowGradient
            recentColors={recentColors}
            onRecentColorPicked={onRecentColorPicked}
            label="Fill"
          />
          <button
            type="button"
            onClick={() => onChange({ fill: 'transparent' })}
            className="text-[10px] px-2 py-1 rounded border border-border hover:bg-accent text-muted-foreground"
            title="Set fill to transparent (no fill)"
          >
            None
          </button>
        </label>
      </div>

      {/* Variant-specific section. Pen + Curvature don't have
          extra knobs in v1; Freeform exposes magnetic snapping +
          a stroke-following width sensitivity slider. */}
      {settings.variant === 'freeform' && (
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase">Freeform</h4>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={settings.freeformMagnetic}
              onChange={(e) => onChange({ freeformMagnetic: e.target.checked })}
              className="w-3.5 h-3.5"
            />
            <span>Magnetic (snap to edges)</span>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground w-14">Width</span>
            <input
              type="range"
              min={0}
              max={100}
              value={settings.freeformWidth}
              onChange={(e) => onChange({ freeformWidth: Number(e.target.value) })}
              className="flex-1"
            />
            <span className="w-8 text-right tabular-nums">{settings.freeformWidth}</span>
          </label>
        </div>
      )}

      {editPathAvailable && onEditActivePath && (
        <button
          type="button"
          onClick={onEditActivePath}
          className="mt-2 px-3 py-1.5 rounded border border-border text-xs hover:bg-accent"
        >
          Edit selected path…
        </button>
      )}
    </div>
  );
}
